import React, { useState } from 'react';
import { ScanResultDto } from '../types/api';
import { StockRow } from './StockRow';

interface ResultsGridProps {
  results: ScanResultDto[];
  isLoading: boolean;
  showNoSignal: boolean;
}

function parseIndicators(r: ScanResultDto): { rsi: number; adx: number } {
  try { const ind = r.indicators ? JSON.parse(r.indicators) : {}; return { rsi: ind.rsi ?? 0, adx: ind.adx ?? 0 }; }
  catch { return { rsi: 0, adx: 0 }; }
}

function sortDesc(a: ScanResultDto, b: ScanResultDto) {
  const ai = parseIndicators(a), bi = parseIndicators(b);
  return bi.rsi - ai.rsi || bi.adx - ai.adx;
}

function sortAsc(a: ScanResultDto, b: ScanResultDto) {
  const ai = parseIndicators(a), bi = parseIndicators(b);
  return ai.rsi - bi.rsi || ai.adx - bi.adx;
}

interface BucketProps {
  title: string;
  icon: string;
  description: string;
  results: ScanResultDto[];
  className: string;
}

function ResultBucket({ title, icon, description, results, className }: BucketProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <article className={`card result-card ${className}`}>
      <div className="bucket-header" onClick={() => setCollapsed(v => !v)}>
        <span className="bucket-icon">{icon}</span>
        <div className="bucket-header-text">
          <h2 className="bucket-title">{title} Signal</h2>
          <p className="bucket-desc">{results.length} stocks &middot; {description}</p>
        </div>
        <span className="bucket-chevron">{collapsed ? '▼' : '▲'}</span>
      </div>

      {!collapsed && (
        results.length === 0 ? (
          <div className="bucket-empty">No stocks</div>
        ) : (
          <div className="result-list">
            {results.map((result, i) => <StockRow key={result.id} result={result} rank={i + 1} />)}
          </div>
        )
      )}
    </article>
  );
}

export function ResultsGrid({ results, isLoading, showNoSignal }: ResultsGridProps) {
  const buy  = results.filter(r => r.classification === 'BUY').sort(sortDesc);
  const hold = results.filter(r => r.classification === 'HOLD').sort(sortDesc);
  const sell = results.filter(r => r.classification === 'SELL').sort(sortAsc);
  const none = results.filter(r => r.classification === 'NONE').sort(sortAsc);

  if (isLoading) {
    return (
      <section className="results-grid">
        <div className="loading-state">
          <div className="spinner" />
          <p>Scanning stock universe…</p>
        </div>
      </section>
    );
  }

  if (results.length === 0) {
    return (
      <section className="results-grid">
        <div className="empty-state">
          <p>No results yet. Click <strong>Run Scan</strong> to analyse the stock universe.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`results-grid ${showNoSignal ? 'four-col' : 'three-col'}`}>
      <ResultBucket title="BUY"       icon="↑" description="Trend &amp; momentum aligned" results={buy}  className="in-criteria"    />
      <ResultBucket title="HOLD"      icon="⏸" description="Holding criteria met"         results={hold} className="getting-better" />
      <ResultBucket title="SELL"      icon="↓" description="Exit signal detected"          results={sell} className="falling-away"   />
      {showNoSignal && (
        <ResultBucket title="No"      icon="—" description="No actionable setup"           results={none} className="no-signal"      />
      )}
    </section>
  );
}
